import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DESIGN_TOKENS } from '../theme';
import LogoMain from '../components/LogoMain';

/**
 * "Gift vouchers and VAT" — the public explainer linked from every purchase confirmation
 * PDF (https://gift.10seat.com/help/en/gift-vouchers-and-vat and
 * https://gift.10seat.com/help/nl/cadeaubonnen-en-btw, optionally with ?country=be|nl).
 *
 * The text is fixed copy in English and Dutch, deliberately not run through the shop's
 * i18n: the language is the one in the URL, because that URL is printed on the document.
 * 10Seat-neutral styling, no venue branding.
 */

export type HelpLanguage = 'en' | 'nl';
type RateCountry = 'be' | 'nl';

const VOUCHER_VAT_HELP_PATHS: Record<HelpLanguage, string> = {
  en: '/help/en/gift-vouchers-and-vat',
  nl: '/help/nl/cadeaubonnen-en-btw',
};

interface Paragraph {
  lead: string;
  text: string;
}

interface ArticleCopy {
  title: string;
  intro: string;
  multiPurpose: Paragraph;
  chargedOnUse: Paragraph;
  countryTabsLabel: string;
  countries: Record<RateCountry, { tab: string; rates: string }>;
  companyHeading: string;
  companyBullets: string[];
  seller: Paragraph;
  questions: Paragraph;
  switchLanguage: string;
}

const COPY: Record<HelpLanguage, ArticleCopy> = {
  en: {
    title: 'Gift vouchers and VAT: why your document has no VAT',
    intro:
      "When you buy a restaurant gift voucher, you receive a purchase confirmation without VAT. That's correct, and here's why.",
    multiPurpose: {
      lead: "It's a multi-purpose voucher.",
      text: 'A restaurant voucher can be spent on food and on drinks, which have different VAT rates. When the voucher is bought, nobody knows what it will be used for. European VAT rules call this a multi-purpose voucher. For this type of voucher, no VAT is charged at the moment of sale.',
    },
    chargedOnUse: {
      lead: 'VAT is charged when the voucher is used.',
      text: 'When the voucher is redeemed, the restaurant registers what was actually consumed on its till and charges the correct VAT then.',
    },
    countryTabsLabel: 'VAT rates by country',
    countries: {
      be: { tab: 'Belgium', rates: 'Belgium: 12% on food, 21% on drinks' },
      nl: { tab: 'Netherlands', rates: 'Netherlands: 9% on food and non-alcoholic drinks, 21% on alcohol' },
    },
    companyHeading: 'What this means for your company',
    companyBullets: [
      'The purchase confirmation is a valid proof of purchase. You can book it as a cost in your accounting.',
      'There is no VAT to recover on the purchase, because none was charged.',
      'It is not a VAT invoice, and you cannot request one for a voucher purchase. No supplier can legally issue one for this type of voucher.',
    ],
    seller: {
      lead: 'Who is the seller?',
      text: "The restaurant. 10Seat provides the booking and payment technology and issues the document on the restaurant's behalf. Your payment goes directly to the restaurant.",
    },
    questions: {
      lead: 'Questions?',
      text: 'Contact the restaurant, or ask your accountant to look up the multi-purpose voucher rules (BE: art. 22ter §2 WBTW; NL: art. 28zi Wet OB).',
    },
    switchLanguage: 'Nederlands',
  },
  nl: {
    title: 'Cadeaubonnen en btw: waarom er geen btw op je aankoopbewijs staat',
    intro:
      'Wanneer je een cadeaubon van een restaurant koopt, krijg je een aankoopbewijs zonder btw. Dat is correct, en zo zit het.',
    multiPurpose: {
      lead: 'Het is een voucher voor meervoudig gebruik.',
      text: 'Een restaurantbon kan besteed worden aan eten en aan drank, met verschillende btw-tarieven. Bij aankoop weet niemand waarvoor de bon gebruikt zal worden. De Europese btw-regels noemen dit een voucher voor meervoudig gebruik. Op dit soort bonnen wordt bij verkoop geen btw aangerekend.',
    },
    chargedOnUse: {
      lead: 'De btw wordt aangerekend bij inwisseling.',
      text: 'Wanneer de bon wordt gebruikt, registreert het restaurant wat er effectief geconsumeerd is op de kassa en rekent het dan de juiste btw aan.',
    },
    countryTabsLabel: 'Btw-tarieven per land',
    countries: {
      be: { tab: 'België', rates: 'België: 12% op eten, 21% op dranken' },
      nl: { tab: 'Nederland', rates: 'Nederland: 9% op eten en alcoholvrije dranken, 21% op alcohol' },
    },
    companyHeading: 'Wat betekent dit voor je bedrijf?',
    companyBullets: [
      'Het aankoopbewijs is een geldig bewijsstuk. Je kan het als kost boeken in je boekhouding.',
      'Er is geen btw te recupereren op de aankoop, want er werd geen btw aangerekend.',
      'Het is geen btw-factuur, en die kan je voor een cadeaubon ook niet aanvragen. Geen enkele leverancier mag die wettelijk uitreiken voor dit type bon.',
    ],
    seller: {
      lead: 'Wie is de verkoper?',
      text: 'Het restaurant. 10Seat levert de reservatie- en betaaltechnologie en maakt het document op in naam van het restaurant. Je betaling gaat rechtstreeks naar het restaurant.',
    },
    questions: {
      lead: 'Vragen?',
      text: 'Neem contact op met het restaurant, of vraag je boekhouder naar de regels voor vouchers voor meervoudig gebruik (BE: art. 22ter §2 WBTW; NL: art. 28zi Wet OB).',
    },
    switchLanguage: 'English',
  },
};

const RATE_COUNTRIES: RateCountry[] = ['be', 'nl'];

function rateCountryFromQuery(value: string | null): RateCountry {
  return (value || '').toLowerCase() === 'nl' ? 'nl' : 'be';
}

const T = DESIGN_TOKENS;

const paragraphStyle: React.CSSProperties = { font: `400 17px/1.7 ${T.fontFamily}`, color: T.body, margin: '0 0 20px' };

/** A paragraph that opens with a bold lead-in sentence. */
const LeadParagraph: React.FC<{ paragraph: Paragraph }> = ({ paragraph }) => (
  <p style={paragraphStyle}>
    <strong style={{ color: T.ink, fontWeight: 600 }}>{paragraph.lead}</strong> {paragraph.text}
  </p>
);

const VoucherVatHelpPage: React.FC<{ lang: HelpLanguage }> = ({ lang }) => {
  const copy = COPY[lang];
  const [searchParams, setSearchParams] = useSearchParams();
  const country = rateCountryFromQuery(searchParams.get('country'));
  const otherLang: HelpLanguage = lang === 'en' ? 'nl' : 'en';

  useEffect(() => {
    document.title = `${copy.title} · 10Seat`;
  }, [copy.title]);

  const selectCountry = (next: RateCountry) => {
    const params = new URLSearchParams(searchParams);
    params.set('country', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col" style={{ background: T.paper, color: T.ink, fontFamily: T.fontFamily }}>
      <header
        className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10"
        style={{ borderBottom: `1px solid ${T.hairline}` }}
      >
        <a href="https://10seat.com" target="_blank" rel="noopener noreferrer" aria-label="10Seat" style={{ color: T.ink }}>
          <LogoMain size="sm" />
        </a>
        <Link
          to={`${VOUCHER_VAT_HELP_PATHS[otherLang]}?country=${country}`}
          hrefLang={otherLang}
          lang={otherLang}
          className="underline underline-offset-2"
          style={{ font: `500 14px/1 ${T.fontFamily}`, color: T.body }}
        >
          {copy.switchLanguage}
        </Link>
      </header>

      <main className="mx-auto w-full max-w-[680px] flex-1 px-5 py-10 sm:px-8 sm:py-16">
        <article lang={lang}>
          <h1
            style={{
              margin: '0 0 20px',
              font: `600 clamp(26px, 5vw, 36px)/1.2 ${T.fontFamily}`,
              letterSpacing: '-.02em',
              color: T.ink,
            }}
          >
            {copy.title}
          </h1>
          <p style={{ ...paragraphStyle, font: `400 19px/1.65 ${T.fontFamily}`, color: T.ink, margin: '0 0 32px' }}>
            {copy.intro}
          </p>

          <LeadParagraph paragraph={copy.multiPurpose} />
          <LeadParagraph paragraph={copy.chargedOnUse} />

          <div
            className="mb-8"
            style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: T.radius }}
          >
            <div role="tablist" aria-label={copy.countryTabsLabel} className="flex" style={{ borderBottom: `1px solid ${T.hairline}` }}>
              {RATE_COUNTRIES.map((code) => {
                const selected = code === country;
                return (
                  <button
                    key={code}
                    type="button"
                    role="tab"
                    id={`rates-tab-${code}`}
                    aria-selected={selected}
                    aria-controls="rates-panel"
                    onClick={() => selectCountry(code)}
                    className="flex-1 sm:flex-none"
                    style={{
                      padding: '12px 20px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${selected ? T.ink : 'transparent'}`,
                      marginBottom: -1,
                      font: `${selected ? 600 : 500} 15px/1.2 ${T.fontFamily}`,
                      color: selected ? T.ink : T.muted,
                      cursor: 'pointer',
                    }}
                  >
                    {copy.countries[code].tab}
                  </button>
                );
              })}
            </div>
            <div id="rates-panel" role="tabpanel" aria-labelledby={`rates-tab-${country}`} className="px-5 py-4">
              <ul className="list-disc pl-5" style={{ margin: 0 }}>
                <li style={{ font: `400 16px/1.6 ${T.fontFamily}`, color: T.ink }}>{copy.countries[country].rates}</li>
              </ul>
            </div>
          </div>

          <h2
            style={{
              margin: '40px 0 14px',
              font: `600 clamp(20px, 3.5vw, 24px)/1.3 ${T.fontFamily}`,
              letterSpacing: '-.01em',
              color: T.ink,
            }}
          >
            {copy.companyHeading}
          </h2>
          <ul className="list-disc pl-6" style={{ margin: '0 0 32px' }}>
            {copy.companyBullets.map((bullet) => (
              <li key={bullet} style={{ font: `400 17px/1.7 ${T.fontFamily}`, color: T.body, marginBottom: 8 }}>
                {bullet}
              </li>
            ))}
          </ul>

          <LeadParagraph paragraph={copy.seller} />
          <LeadParagraph paragraph={copy.questions} />
        </article>
      </main>

      <footer className="px-5 py-6 sm:px-10" style={{ borderTop: `1px solid ${T.hairline}` }}>
        <a
          href="https://10seat.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ font: `400 13px/1.5 ${T.fontFamily}`, color: T.label }}
        >
          10seat.com
        </a>
      </footer>
    </div>
  );
};

export default VoucherVatHelpPage;
